import React, { useCallback, useEffect, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService, type WorkerMessage, type WorkerMessageThread } from '../../../../services/worker'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobileLoadingState,
  MobileSecondaryButton,
} from './WorkerMobilePrimitives'

/**
 * Worker messaging at mobile parity with the Android Messages tab.
 *
 * Thread visibility, reply rights, and participant scope are all decided by
 * the backend's existing tenant/relationship rules — `canReply` comes from the
 * server and is never inferred from thread status here.
 */

export const WorkerMobileMessages: React.FC<{
  state: WorkerWorkspaceState
  initialThreadId?: string | null
  onConsumeInitialThread?: () => void
}> = ({ state, initialThreadId, onConsumeInitialThread }) => {
  const { t, formatDateTime } = useI18n()
  const { data, load } = state
  const [openThread, setOpenThread] = useState<WorkerMessageThread | null>(null)
  const [messages, setMessages] = useState<WorkerMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const openThreadById = useCallback(async (thread: WorkerMessageThread) => {
    setOpenThread(thread)
    setLoading(true)
    setError('')
    try {
      setMessages(await workerService.getMessages(thread.id))
      if (thread.unreadCount) {
        await workerService.markThreadRead(thread.id)
        await load()
      }
    } catch (err) {
      setError((err as any)?.response?.data?.message || t('This conversation could not be opened.'))
    } finally {
      setLoading(false)
    }
  }, [load, t])

  // Notification deep link: a push/notification click can name a thread.
  useEffect(() => {
    if (!initialThreadId) return
    const thread = data.threads.find((row) => row.id === initialThreadId)
    if (!thread) return
    void openThreadById(thread)
    onConsumeInitialThread?.()
  }, [initialThreadId, data.threads, openThreadById, onConsumeInitialThread])

  const send = async () => {
    if (!openThread || !draft.trim()) return
    setSending(true)
    setError('')
    try {
      await workerService.sendMessage(openThread.id, draft.trim())
      setDraft('')
      setMessages(await workerService.getMessages(openThread.id))
      await load()
    } catch (err) {
      setError((err as any)?.response?.data?.message || t('Your message could not be sent.'))
    } finally {
      setSending(false)
    }
  }

  if (openThread) {
    return (
      <div className="wm-screen wm-screen--thread" data-testid="worker-mobile-thread">
        <div className="wm-thread-bar">
          <button type="button" className="wm-icon-button" onClick={() => { setOpenThread(null); setMessages([]) }} aria-label={t('Back')}>‹</button>
          <div>
            <strong>{openThread.title || t('Conversation')}</strong>
            <small>{t(String(openThread.status || 'active').replace(/_/g, ' '))}</small>
          </div>
        </div>
        {error ? <p className="wm-inline-error" role="alert">{error}</p> : null}
        {loading ? <MobileLoadingState label={t('Loading conversation…')} /> : (
          <ol className="wm-messages">
            {messages.map((message) => (
              <li key={message.id} className="wm-message">
                <div className="wm-message__meta">
                  <strong>{message.senderName || t('Team')}</strong>
                  {message.createdAt ? <small>{formatDateTime(message.createdAt, { dateStyle: 'short', timeStyle: 'short' })}</small> : null}
                </div>
                <p>{message.body}</p>
              </li>
            ))}
            {!messages.length ? <MobileEmptyState title={t('No messages yet')} detail={t('Start the conversation below.')} /> : null}
          </ol>
        )}
        {openThread.canReply ? (
          <form className="wm-composer" onSubmit={(event) => { event.preventDefault(); void send() }}>
            <label className="wm-visually-hidden" htmlFor="worker-mobile-message-input">{t('Your message')}</label>
            <textarea
              id="worker-mobile-message-input"
              rows={2}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('Write a message')}
            />
            <button type="submit" className="wm-button wm-button--primary" data-tone="info" disabled={sending || !draft.trim()}>
              {t('Send')}
            </button>
          </form>
        ) : (
          <p className="wm-guidance">{t('Replies are not enabled for this conversation.')}</p>
        )}
      </div>
    )
  }

  return (
    <div className="wm-screen" data-testid="worker-mobile-messages">
      {data.threads.length ? data.threads.map((thread) => (
        <MobileCard key={thread.id} testId="worker-mobile-thread-card">
          <div className="wm-card__heading">
            <div>
              <h3>{thread.title || t('Conversation')}</h3>
              {thread.lastMessageBody ? <p className="wm-preview">{thread.lastMessageBody}</p> : null}
              {thread.lastMessageAt ? <small>{formatDateTime(thread.lastMessageAt, { dateStyle: 'short', timeStyle: 'short' })}</small> : null}
            </div>
            {thread.unreadCount ? <MobileChip tone="critical">{String(thread.unreadCount)}</MobileChip> : null}
          </div>
          <MobileSecondaryButton label={t('Open')} onClick={() => void openThreadById(thread)} testId="worker-mobile-open-thread" />
        </MobileCard>
      )) : (
        <MobileEmptyState title={t('No conversations yet')} detail={t('Messages from your supervisors and agency appear here.')} />
      )}
    </div>
  )
}
